import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Send, XCircle, CheckCircle, Clock, Lock } from 'lucide-react';

export default function TicketDetailPage() {
  const { id } = useParams();
  const { user, isAdmin } = useAuth();
  const lang = user?.language || 'it';
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const load = async () => { try { const { data } = await api.get(`/support/${id}`); setTicket(data.ticket); setMessages(data.messages); } catch (e) {} finally { setLoading(false); } };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/support/${id}/reply`, { message: reply }); setReply(''); load(); }
    catch (err) { toast.error('Errore'); }
  };

  const changeStatus = async (newStatus) => {
    const labels = { resolved: 'Risolvere', closed: 'Chiudere', in_progress: 'Mettere in lavorazione', open: 'Riaprire' };
    if (!window.confirm(`${labels[newStatus] || 'Cambiare stato'} questo ticket?`)) return;
    try {
      await api.put(`/support/${id}/status`, { status: newStatus });
      toast.success(`Ticket ${newStatus === 'closed' ? 'chiuso' : newStatus === 'resolved' ? 'risolto' : newStatus}`);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pn-red" /></div>;
  if (!ticket) return <p>Non trovato</p>;

  const statusLabels = { open: 'Aperto', in_progress: 'In Lavorazione', resolved: 'Risolto', closed: 'Chiuso' };
  const statusColors = { open: 'bg-green-500', in_progress: 'bg-blue-500', resolved: 'bg-gray-500', closed: 'bg-gray-700' };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-3 mb-4">
        <Link to={isAdmin ? '/admin/support' : '/support'} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft size={20} /></Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-pn-dark">{ticket.subject}</h1>
          <p className="text-sm text-gray-400">{ticket.category} · <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full text-white ${statusColors[ticket.status]}`}>{statusLabels[ticket.status]}</span></p>
        </div>
      </div>

      {/* Admin controls */}
      {isAdmin && (
        <div className="flex flex-wrap gap-2 mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <span className="text-xs text-yellow-700 font-medium mr-2 flex items-center">Azioni Admin:</span>
          {ticket.status !== 'in_progress' && <button onClick={() => changeStatus('in_progress')} className="text-xs px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-1"><Clock size={12} />In Lavorazione</button>}
          {ticket.status !== 'resolved' && <button onClick={() => changeStatus('resolved')} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"><CheckCircle size={12} />Risolvi</button>}
          {ticket.status !== 'closed' && <button onClick={() => changeStatus('closed')} className="text-xs px-3 py-1.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 flex items-center gap-1"><Lock size={12} />Chiudi Ticket</button>}
          {['closed', 'resolved'].includes(ticket.status) && <button onClick={() => changeStatus('open')} className="text-xs px-3 py-1.5 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center gap-1"><XCircle size={12} />Riapri</button>}
        </div>
      )}

      <div className="card p-4 mb-4 space-y-4 max-h-[60vh] overflow-y-auto">
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.sender_id === user.id ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.sender_id === user.id ? 'bg-pn-red text-white rounded-br-md' : 'bg-gray-100 rounded-bl-md'}`}>
              <p className="text-sm whitespace-pre-wrap">{m.message}</p>
              <p className={`text-[10px] mt-1 ${m.sender_id === user.id ? 'text-white/60' : 'text-gray-400'}`}>{m.first_name} · {new Date(m.created_at).toLocaleString()}</p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {['open', 'in_progress'].includes(ticket.status) ? (
        <div className="flex gap-3">
          <input className="input-field flex-1" placeholder={lang === 'it' ? 'Scrivi un messaggio...' : 'Write a message...'} value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendReply()} />
          <button onClick={sendReply} className="btn-primary px-4"><Send size={18} /></button>
        </div>
      ) : (
        <p className="text-center text-sm text-gray-400 py-4">Ticket {statusLabels[ticket.status]?.toLowerCase()} — {isAdmin ? 'puoi riaprirlo sopra' : 'contatta il supporto per riaprirlo'}</p>
      )}
    </div>
  );
}
